import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../store';
import { fetchConfigTemplateStates } from '../../../store/slices/configTemplatesListSlice';
import {
    CpmButton,
    CpmIconButton,
    CpmInlineErrorBanner,
    CpmModal,
    CpmTooltip,
} from '../../../components/design-system';
import {
    applyConfigTemplate,
    getConfigTemplateOverlayKeys,
} from '../../../services/configTemplateService';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type {
    ConfigTemplateApplyResult,
    ConfigTemplateDraft,
    ConfigTemplateFieldSchemaResponse,
    ConfigTemplateLevel,
    ConfigTemplateOverlayKeys,
} from '../../../types/configTemplate.types';
import {
    applyActionLabel,
    applyConfirmLabel,
    applyFooterNote,
    applyRetryLabel,
    appliedProgress,
    CONFIG_TEMPLATE_COPY,
} from '../configTemplateCopy';
import { applyPreviewLabels } from '../utils/fieldMeta';
import { useTargetPicker } from '../hooks/useTargetPicker';
import ApplyReceipt from './ApplyReceipt';
import OverlayPreview from './OverlayPreview';
import { PickerPulseLine } from './PickerFilterPopover';
import TargetPickerList from './TargetPickerList';

interface ApplyFlowProps {
    open: boolean;
    onClose: () => void;
    level: ConfigTemplateLevel;
    draft: ConfigTemplateDraft;
    schema?: ConfigTemplateFieldSchemaResponse | null;
    states: CpmState[];
    statesStatus?: 'idle' | 'loading' | 'success' | 'error';
}

const CHUNK = 50;

function mergeResults(
    acc: ConfigTemplateApplyResult,
    next: ConfigTemplateApplyResult
): ConfigTemplateApplyResult {
    return {
        applied: [...acc.applied, ...next.applied],
        failed: [...acc.failed, ...next.failed],
        overlayKeys: next.overlayKeys ?? acc.overlayKeys,
        templateId: next.templateId ?? acc.templateId,
    };
}

function CloseIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
        </svg>
    );
}

function RetryIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M21 12a9 9 0 1 1-2.6-6.3" strokeLinecap="round" />
            <path d="M21 3v6h-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function AlertCircleIcon() {
    return (
        <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
            <circle cx="12" cy="12" r="9" />
        </svg>
    );
}

export default function ApplyFlow({
    open,
    onClose,
    level,
    draft,
    schema,
    states,
    statesStatus = 'success',
}: ApplyFlowProps) {
    const dispatch = useAppDispatch();
    const defaultState =
        !draft.identity.allStates && draft.identity.states.length === 1
            ? draft.identity.states[0]
            : undefined;
    const picker = useTargetPicker({
        level,
        mode: 'apply',
        defaultState,
        open,
    });
    const [overlay, setOverlay] = useState<ConfigTemplateOverlayKeys | null>(null);
    const [overlayFailed, setOverlayFailed] = useState(false);
    const [phase, setPhase] = useState<'pick' | 'applying' | 'receipt'>('pick');
    const [progress, setProgress] = useState('');
    const [result, setResult] = useState<ConfigTemplateApplyResult | null>(null);
    const [applyFailed, setApplyFailed] = useState(false);
    const [retrying, setRetrying] = useState(false);

    const loadOverlay = () => {
        if (!draft.id) return;
        void getConfigTemplateOverlayKeys({ id: draft.id })
            .then((data) => {
                setOverlay(data);
                setOverlayFailed(false);
            })
            .catch(() => {
                setOverlayFailed(true);
            });
    };

    useEffect(() => {
        if (!open || !draft.id) return;
        setPhase('pick');
        setResult(null);
        setApplyFailed(false);
        setRetrying(false);
        setOverlay(null);
        setOverlayFailed(false);
        loadOverlay();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [draft.id, open]);

    const previewLabels = useMemo(() => {
        const fromSchema = applyPreviewLabels(schema, draft.included);
        if (fromSchema.length) return fromSchema;
        return overlay?.groups.flatMap((group) => group.labels) ?? [];
    }, [draft.included, overlay, schema]);
    const writeCount = overlay?.count ?? draft.included.length;
    const emptyOverlay = overlay != null && overlay.count === 0;
    const writing = phase === 'applying';
    const selectedCount = picker.selectedCount;

    const runChunks = async (ids: string[], fromReceipt = false) => {
        if (!draft.id) return;
        if (fromReceipt) setRetrying(true);
        else setPhase('applying');
        setApplyFailed(false);
        let acc: ConfigTemplateApplyResult = { applied: [], failed: [] };
        const total = ids.length;
        for (let i = 0; i < ids.length; i += CHUNK) {
            const chunk = ids.slice(i, i + CHUNK);
            setProgress(appliedProgress(acc.applied.length + acc.failed.length, total));
            const next = await applyConfigTemplate({
                id: draft.id,
                storeBrandIds: level === 'STORE_BRAND' ? chunk : undefined,
                storeIds: level === 'STORE' ? chunk : undefined,
            });
            acc = mergeResults(acc, next);
            setProgress(appliedProgress(acc.applied.length + acc.failed.length, total));
        }
        setResult(acc);
        setPhase('receipt');
        setRetrying(false);
    };

    const applySelected = async () => {
        try {
            const { ids, total } = await picker.collectApplyIds();
            if (ids.length === 0) return;
            if (total > 500 && !window.confirm(CONFIG_TEMPLATE_COPY.CONFIRM_LARGE_APPLY)) {
                return;
            }
            await runChunks(ids);
        } catch {
            setApplyFailed(true);
            setPhase('pick');
        }
    };

    const retryFailed = async () => {
        if (!result?.failed.length) return;
        try {
            await runChunks(
                result.failed.map((row) => row.id),
                true
            );
        } catch {
            setApplyFailed(true);
            setRetrying(false);
        }
    };

    return (
        <CpmModal
            open={open}
            onClose={onClose}
            maxWidth={720}
            closeOnEscape={!writing}
            closeOnBackdrop={!writing}
        >
            <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 104px)' }}>
                <div
                    className="flex items-start"
                    style={{
                        gap: 14,
                        padding: '12px 20px 10px',
                        borderBottom: '1px solid var(--cpm-border-default)',
                    }}
                >
                    <div className="min-w-0 flex-1">
                        <div
                            style={{
                                fontSize: 17,
                                fontWeight: 800,
                                letterSpacing: '-0.02em',
                                color: 'var(--cpm-text-primary)',
                            }}
                        >
                            {applyActionLabel(level)}
                        </div>
                        <div className="flex items-center" style={{ gap: 7, marginTop: 2 }}>
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                {draft.identity.name.trim() || CONFIG_TEMPLATE_COPY.UNTITLED}
                            </span>
                            <CpmTooltip
                                title={CONFIG_TEMPLATE_COPY.APPLY}
                                content={CONFIG_TEMPLATE_COPY.APPLY_TIP}
                                align="start"
                                fit
                                maxWidth={268}
                            >
                                <span
                                    className="inline-flex items-center"
                                    style={{ color: 'var(--cpm-text-tertiary)' }}
                                >
                                    <AlertCircleIcon />
                                </span>
                            </CpmTooltip>
                        </div>
                    </div>
                    <CpmIconButton
                        icon={<CloseIcon />}
                        label="Close"
                        size="md"
                        disabled={writing}
                        onClick={onClose}
                    />
                </div>

                {phase === 'receipt' && result ? (
                    <>
                        <div style={{ padding: '12px 20px' }}>
                            <ApplyReceipt level={level} result={result} />
                            {applyFailed ? (
                                <div style={{ marginTop: 8 }}>
                                    <CpmInlineErrorBanner
                                        compact
                                        message={CONFIG_TEMPLATE_COPY.APPLY_REQUEST_FAIL}
                                        onRetry={() => void retryFailed()}
                                        retryLabel={CONFIG_TEMPLATE_COPY.RETRY}
                                    />
                                </div>
                            ) : null}
                        </div>
                        <div
                            className="flex shrink-0 items-center justify-end"
                            style={{
                                gap: 9,
                                padding: '9px 20px',
                                borderTop: '1px solid var(--cpm-border-default)',
                                background: 'rgba(249,249,252,0.88)',
                            }}
                        >
                            {retrying ? (
                                <PickerPulseLine>{CONFIG_TEMPLATE_COPY.APPLY_WRITING}</PickerPulseLine>
                            ) : null}
                            {result.failed.length > 0 ? (
                                <CpmButton
                                    variant="outline"
                                    size="md"
                                    leadingIcon={<RetryIcon />}
                                    disabled={retrying}
                                    onClick={() => void retryFailed()}
                                    style={{ color: 'var(--cpm-text-primary)' }}
                                >
                                    {applyRetryLabel(result.failed.length)}
                                </CpmButton>
                            ) : null}
                            <CpmButton variant="navy" size="md" disabled={retrying} onClick={onClose}>
                                {CONFIG_TEMPLATE_COPY.DONE}
                            </CpmButton>
                        </div>
                    </>
                ) : (
                    <>
                        {overlayFailed ? (
                            <div style={{ padding: '8px 20px 0' }}>
                                <CpmInlineErrorBanner
                                    compact
                                    message={CONFIG_TEMPLATE_COPY.OVERLAY_FAIL}
                                    onRetry={loadOverlay}
                                    retryLabel={CONFIG_TEMPLATE_COPY.RETRY}
                                />
                            </div>
                        ) : (
                            <OverlayPreview labels={previewLabels} count={writeCount} />
                        )}
                        {emptyOverlay ? (
                            <div
                                style={{
                                    padding: '12px 20px',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: 'var(--cpm-shortfall-text)',
                                }}
                            >
                                There is nothing in this template to write. Add settings, or copy
                                from a live store brand or store.
                            </div>
                        ) : (
                            <TargetPickerList
                                level={level}
                                picker={picker}
                                states={states}
                                single={false}
                                fill
                                compact
                                disabled={writing}
                                statesStatus={statesStatus}
                                onRetryStates={() => void dispatch(fetchConfigTemplateStates())}
                            />
                        )}
                        {applyFailed ? (
                            <div style={{ padding: '0 20px 4px' }}>
                                <CpmInlineErrorBanner
                                    compact
                                    message={CONFIG_TEMPLATE_COPY.APPLY_REQUEST_FAIL}
                                    onRetry={() => void applySelected()}
                                    retryLabel={CONFIG_TEMPLATE_COPY.RETRY}
                                />
                            </div>
                        ) : null}
                        <div
                            className="flex shrink-0 items-center justify-between"
                            style={{
                                gap: 14,
                                padding: '9px 20px',
                                borderTop: '1px solid var(--cpm-border-default)',
                                background: 'rgba(249,249,252,0.88)',
                                backdropFilter: 'blur(14px)',
                                boxShadow: '0 -6px 18px -8px rgba(14,8,88,0.14)',
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                {applyFooterNote(selectedCount)}
                            </span>
                            <div className="flex items-center" style={{ gap: 9 }}>
                                {writing ? (
                                    <PickerPulseLine>{progress || CONFIG_TEMPLATE_COPY.APPLY_WRITING}</PickerPulseLine>
                                ) : null}
                                <CpmButton
                                    variant="outline"
                                    size="md"
                                    disabled={writing}
                                    onClick={onClose}
                                    style={{ color: 'var(--cpm-text-primary)' }}
                                >
                                    {CONFIG_TEMPLATE_COPY.COPY_CANCEL}
                                </CpmButton>
                                <CpmButton
                                    variant="navy"
                                    size="md"
                                    disabled={
                                        writing ||
                                        selectedCount === 0 ||
                                        emptyOverlay ||
                                        picker.status === 'loading'
                                    }
                                    isLoading={writing}
                                    onClick={() => void applySelected()}
                                >
                                    {applyConfirmLabel(level, selectedCount)}
                                </CpmButton>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </CpmModal>
    );
}
